import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { OFFERS } from "@/lib/offers";

const STORAGE_KEY = "zwahili-offers-popup-dismissed-at";
const SUPPRESS_DAYS = 7;
const DWELL_MS = 20000;
const SCROLL_TRIGGER_PCT = 0.4;

const SWIPE_DISMISS_THRESHOLD = 100; // px

const SpecialOffersPopup = () => {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const touchStartY = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const forceShow = new URLSearchParams(window.location.search).has("showOffers");
    if (forceShow) {
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      setOpen(true);
      return;
    }
    try {
      const dismissedAt = localStorage.getItem(STORAGE_KEY);
      if (dismissedAt) {
        const elapsed = Date.now() - Number(dismissedAt);
        if (Number.isFinite(elapsed) && elapsed < SUPPRESS_DAYS * 24 * 60 * 60 * 1000) {
          return;
        }
      }
    } catch {
      // localStorage unavailable; show anyway
    }

    let done = false;
    const trigger = () => {
      if (done) return;
      done = true;
      cleanup();
      setOpen(true);
    };

    const onScroll = () => {
      const doc = document.documentElement;
      const scrolled = window.scrollY + window.innerHeight;
      const height = Math.max(doc.scrollHeight, 1);
      if (scrolled / height >= SCROLL_TRIGGER_PCT) trigger();
    };

    const onExitIntent = (e: MouseEvent) => {
      // Fires when the cursor exits the top of the viewport (desktop exit-intent).
      if (e.clientY <= 0) trigger();
    };

    const timerId = window.setTimeout(trigger, DWELL_MS);
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("mouseout", onExitIntent);

    function cleanup() {
      window.clearTimeout(timerId);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("mouseout", onExitIntent);
    }
    return cleanup;
  }, []);


  const handleClose = (next: boolean) => {
    setOpen(next);
    if (!next) {
      try {
        localStorage.setItem(STORAGE_KEY, String(Date.now()));
      } catch {
        // ignore
      }
    }
  };

  const onTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return;
    touchStartY.current = e.touches[0].clientY;
    setIsDragging(true);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!isMobile || touchStartY.current === null) return;
    const delta = e.touches[0].clientY - touchStartY.current;
    // Only allow downward drag, with light resistance for upward
    setDragY(delta > 0 ? delta : delta / 4);
  };

  const onTouchEnd = () => {
    if (!isMobile) return;
    setIsDragging(false);
    if (dragY > SWIPE_DISMISS_THRESHOLD) {
      handleClose(false);
    }
    setDragY(0);
    touchStartY.current = null;
  };

  const dragStyle =
    isMobile && (isDragging || dragY !== 0)
      ? {
          transform: `translate(-50%, calc(-50% + ${dragY}px))`,
          transition: isDragging ? "none" : "transform 200ms ease-out",
          opacity: Math.max(1 - Math.max(dragY, 0) / 400, 0.4),
        }
      : undefined;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="w-[calc(100vw-1.5rem)] sm:w-[calc(100vw-2rem)] max-w-3xl max-h-[90vh] overflow-y-auto bg-white text-foreground border-border/50 p-0 [&>button:not([data-custom-close])]:hidden touch-pan-x"
        aria-label="Special offers"
        style={dragStyle}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      >
        {isMobile && (
          <div
            className="flex justify-center pt-2 pb-1 md:hidden"
            aria-hidden="true"
          >
            <span className="block h-1 w-10 rounded-full bg-foreground/20" />
          </div>
        )}
        <button
          type="button"
          data-custom-close
          onClick={() => handleClose(false)}
          aria-label="Close special offers popup"
          className="fixed sm:absolute right-3 top-3 z-30 inline-flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-full bg-foreground text-white shadow-lg hover:bg-foreground/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-white transition-colors"
        >
          <X className="h-5 w-5" strokeWidth={2.75} aria-hidden="true" />
        </button>
        <div className="px-5 sm:px-6 pt-5 sm:pt-6 pb-3 sm:pb-4 text-center">
          <p className="eyebrow !text-gold">Limited-Time Specials</p>
          <DialogTitle className="font-heading text-xl sm:text-2xl md:text-3xl tracking-wider text-foreground mt-2">
            ESCAPE TO ZWAHILI
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Curated packages for your next bushveld getaway.
          </DialogDescription>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border/30 px-4 sm:px-6 pb-5 sm:pb-6">
          {OFFERS.map((offer) => (
            <div key={offer.slug} className="bg-white flex flex-row md:flex-col gap-3 md:gap-0">
              <div className="aspect-square w-28 sm:w-36 shrink-0 md:w-full md:aspect-[4/3] overflow-hidden rounded md:rounded-none">
                <img
                  src={offer.image}
                  srcSet={offer.imageSrcSet}
                  sizes="(min-width: 768px) 360px, 144px"
                  alt={offer.imageAlt}
                  width={800}
                  height={600}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="p-1 md:p-4 flex flex-col flex-1 min-w-0">
                <p className="text-[10px] tracking-[0.2em] uppercase text-gold font-body font-medium">
                  {offer.eyebrow}
                </p>
                <h3 className="font-heading text-base sm:text-lg tracking-wider text-foreground mt-1 mb-1.5 md:mb-2">
                  {offer.title.toUpperCase()}
                </h3>
                {offer.price && <p className="text-xs text-muted-foreground mb-0.5 md:mb-1">{offer.price}</p>}
                <p className="text-[11px] text-muted-foreground/80 mb-2 md:mb-4">{offer.validity}</p>
                <Link
                  to="/offers"
                  onClick={() => handleClose(false)}
                  className="mt-auto"
                  aria-label={`More info about ${offer.title}`}
                >
                  <Button variant="gold" className="w-full text-xs min-h-10 md:min-h-11">
                    MORE INFO
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
        <div className="px-5 sm:px-6 pb-5 sm:pb-6 -mt-2 text-center">
          <p className="tracking-wider font-medium text-foreground text-[11px] sm:text-xs">
            <span className="inline-block mr-2 align-[-1px]">⭐</span>
            Rated 4.6 on TripAdvisor by our guests
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SpecialOffersPopup;
